import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MainLayout from './components/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { TabProvider } from './context/TabContext';
import { hasPermission, PERMS } from './utils/permissions';

// 路由级代码分割（2026-10-13 性能优化）：非首屏页面改懒加载，
// 首屏 JS 从 ~1.78MB 降到几百KB，弱网/丢包环境下打开显著变快。
const VehicleList = lazy(() => import('./pages/VehicleList'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
const DriverList = lazy(() => import('./pages/DriverList'));
const DriverDetail = lazy(() => import('./pages/DriverDetail'));
const InsuranceList = lazy(() => import('./pages/InsuranceList'));
const InsuranceCompanyList = lazy(() => import('./pages/InsuranceCompanyList'));
const InsuranceSettlement = lazy(() => import('./pages/InsuranceSettlement'));
const RebateList = lazy(() => import('./pages/RebateList'));
const RebateStatList = lazy(() => import('./pages/RebateStatList'));
const InspectionList = lazy(() => import('./pages/InspectionList'));
const CompanyList = lazy(() => import('./pages/CompanyList'));
const UserList = lazy(() => import('./pages/UserList'));
const OperationLogList = lazy(() => import('./pages/OperationLogList'));
const SubmissionList = lazy(() => import('./pages/SubmissionList'));
const AuditCenter = lazy(() => import('./pages/AuditCenter'));
const SubmissionForm = lazy(() => import('./pages/SubmissionForm'));
const RecycleBin = lazy(() => import('./pages/RecycleBin'));
// ⭐ 2026-10-25：我的申请（审计：提交了申请没有任何查看入口）
const MyRequests = lazy(() => import('./pages/MyRequests'));
const TodoList = lazy(() => import('./pages/TodoList'));
const InsuranceRebate = lazy(() => import('./pages/InsuranceRebate'));
const StaffWorkload = lazy(() => import('./pages/StaffWorkload'));
const DriverTraining = lazy(() => import('./pages/DriverTraining'));
const CourseManage = lazy(() => import('./pages/CourseManage'));
const CourseOrders = lazy(() => import('./pages/CourseOrders'));
const TrainingVerification = lazy(() => import('./pages/TrainingVerification'));
const DriverTransfer = lazy(() => import('./pages/DriverTransfer'));
const ArchiveSync = lazy(() => import('./pages/ArchiveSync'));
const SubmissionDetail = lazy(() => import('./pages/SubmissionDetail'));
const MaterialConfig = lazy(() => import('./pages/MaterialConfig'));

function PageLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
      <Spin size="large" />
    </div>
  );
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

function GuardedRoute({ children, perm }) {
  if (!perm) return children;
  return hasPermission(perm) ? children : <Navigate to="/" />;
}

export default function App() {
  // ⭐ 2026-10-23 合规整改：文件签名就绪后触发全局重渲染
  //   背景：敏感图片改用 5 分钟短期签名（不再用 ?token=<7天登录token>）
  //   首次渲染时签名还在异步获取 → 图片会 401，签名到了这里重渲染一次就好了
  //   这样 16 处 fileUrl() 调用点一行都不用改
  const [, forceTick] = useState(0);
  useEffect(() => {
    const handler = () => forceTick((t) => t + 1);
    window.addEventListener('file-sig-ready', handler);
    return () => window.removeEventListener('file-sig-ready', handler);
  }, []);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 8,
          fontSize: 14,
          colorBgLayout: '#f5f7fa',
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif",
        },
        components: {
          Card: { paddingLG: 20 },
          Table: { headerBg: '#f8fafc', rowHoverBg: '#f0f7ff' },
          Button: { controlHeight: 34 },
        },
      }}
    >
      <BrowserRouter>
        <TabProvider>
        <ErrorBoundary>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<PrivateRoute><MainLayout /></PrivateRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="vehicles" element={<GuardedRoute perm={PERMS.VEHICLE_VIEW}><VehicleList /></GuardedRoute>} />
            <Route path="vehicles/new" element={<GuardedRoute perm={PERMS.VEHICLE_CREATE}><VehicleDetail /></GuardedRoute>} />
            <Route path="vehicles/:id" element={<GuardedRoute perm={PERMS.VEHICLE_VIEW}><VehicleDetail /></GuardedRoute>} />
            <Route path="drivers" element={<GuardedRoute perm={PERMS.VEHICLE_VIEW}><DriverList /></GuardedRoute>} />
            <Route path="drivers/:id" element={<GuardedRoute perm={PERMS.VEHICLE_VIEW}><DriverDetail /></GuardedRoute>} />
            <Route path="insurance" element={<GuardedRoute perm={PERMS.INSURANCE_VIEW}><InsuranceList /></GuardedRoute>} />
            <Route path="insurance/companies" element={<GuardedRoute perm={PERMS.INSURANCE_COMPANY_VIEW}><InsuranceCompanyList /></GuardedRoute>} />
            <Route path="insurance/settlement" element={<GuardedRoute perm={PERMS.SETTLEMENT_VIEW}><InsuranceSettlement /></GuardedRoute>} />
            <Route path="rebates" element={<GuardedRoute perm={PERMS.REBATE_VIEW}><InsuranceRebate /></GuardedRoute>} />
            {/* 2026-11-03 员工工作量（仅老板，后端接口也会拦一次） */}
            <Route path="staff-workload" element={<GuardedRoute perm={PERMS.USER_MANAGE}><StaffWorkload /></GuardedRoute>} />
            <Route path="rebates/stats" element={<Navigate to="/rebates?tab=stats" replace />} />
            <Route path="inspections" element={<GuardedRoute perm={PERMS.INSPECTION_VIEW}><InspectionList /></GuardedRoute>} />
            <Route path="users" element={<GuardedRoute perm={PERMS.USER_MANAGE}><UserList /></GuardedRoute>} />
            <Route path="logs" element={<GuardedRoute perm={PERMS.USER_MANAGE}><OperationLogList /></GuardedRoute>} />
            {/* 2026-10-19 别名：有人习惯输 /operation-logs，重定向到正式路由 */}
            <Route path="operation-logs" element={<Navigate to="/logs" replace />} />
            <Route path="companies" element={<GuardedRoute perm={PERMS.COMPANY_VIEW}><CompanyList /></GuardedRoute>} />
            <Route path="submissions" element={<GuardedRoute perm={PERMS.SUBMISSION_VIEW}><SubmissionList /></GuardedRoute>} />
            <Route path="submissions/:id" element={<GuardedRoute perm={PERMS.SUBMISSION_VIEW}><SubmissionDetail /></GuardedRoute>} />
            <Route path="delete-requests" element={<Navigate to="/audit" replace />} />
            <Route path="recycle-bin" element={<GuardedRoute perm={PERMS.VEHICLE_DELETE}><RecycleBin /></GuardedRoute>} />
            {/* ⭐ 2026-10-25：我的申请——所有人可看自己的申请进度 */}
            <Route path="my-requests" element={<MyRequests />} />
            <Route path="reminder-tasks" element={<Navigate to="/todos" replace />} />
            <Route path="todos" element={<GuardedRoute perm={PERMS.INSURANCE_VIEW}><TodoList /></GuardedRoute>} />
            <Route path="driver-training" element={<GuardedRoute perm={PERMS.DRIVER_VIEW}><DriverTraining /></GuardedRoute>} />
            <Route path="course-manage" element={<GuardedRoute perm={PERMS.TRAINING_MANAGE}><CourseManage /></GuardedRoute>} />
            <Route path="course-orders" element={<GuardedRoute perm={PERMS.TRAINING_VIEW}><CourseOrders /></GuardedRoute>} />
            <Route path="training-verification" element={<GuardedRoute perm={PERMS.TRAINING_VIEW}><TrainingVerification /></GuardedRoute>} />
            <Route path="driver-transfer" element={<GuardedRoute perm={PERMS.VEHICLE_EDIT}><DriverTransfer /></GuardedRoute>} />
            <Route path="archive-sync" element={<GuardedRoute perm={PERMS.USER_MANAGE}><ArchiveSync /></GuardedRoute>} />
            <Route path="material-config" element={<GuardedRoute perm={PERMS.USER_MANAGE}><MaterialConfig /></GuardedRoute>} />
            <Route path="audit" element={<GuardedRoute perm={PERMS.VEHICLE_VIEW}><AuditCenter /></GuardedRoute>} />
          </Route>
          <Route path="/submission/form" element={<SubmissionForm />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        </TabProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}
